// eslint-disable-next-line @typescript-eslint/naming-convention
type MAX_RECURSIVE_DEPTH = [1, 1, 1, 1, 1, 1, 1];

type Tail<T extends any[]> = T extends [any, ...infer R] ? R : [];

export type Paths<T, D extends any[] = MAX_RECURSIVE_DEPTH> = D['length'] extends 0
  ? string
  : T extends readonly (infer U)[]
    ? `${number}` | `${number}.${Paths<U, Tail<D>>}`
    : T extends object
      ? { [K in keyof T & (string | number)]: `${K}` | `${K}.${Paths<T[K], Tail<D>>}` }[keyof T & (string | number)]
      : never;

export type PathValue<T, P extends string, D extends any[] = MAX_RECURSIVE_DEPTH> = D['length'] extends 0
  ? unknown
  : T extends readonly (infer U)[]
    ? P extends `${infer H}.${infer R}`
      ? H extends `${number}`
        ? PathValue<U, R, Tail<D>>
        : unknown
      : P extends `${number}`
        ? U
        : unknown
    : P extends `${infer H}.${infer R}`
      ? H extends keyof T
        ? PathValue<T[H], R, Tail<D>>
        : unknown
      : P extends keyof T
        ? T[P]
        : unknown;

/**
 * Immutably sets a nested value in an object at the specified path.
 *
 * This function creates a new object with the specified path updated to the new value,
 * leaving the original object unchanged.
 * @param obj - The original object to update.
 * @param path - The dot-separated path to the property to set.
 * @param value - The new value to set at the specified path.
 * @return A new object with the updated value at the specified path.
 * */
export const setNestedValue = <T, K extends Paths<T>>(obj: T, path: K, value: PathValue<T, K>) => {
  const setValue = (string, obj, value) => {
    const [current, ...rest] = string.split('.');
    if (rest.length >= 1) {
      // eslint-disable-next-line no-param-reassign
      setValue(rest.join('.'), (obj[current] = obj[current] || {}), value);
    } else {
      // eslint-disable-next-line no-param-reassign
      obj[current] = value;
    }
    return obj;
  };
  return setValue(path, structuredClone(obj), value) as T;
};
